import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

interface OfficeLocationState {
  data: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "delete" | "add" | "find" | "list" | "status" | null;
}

const officeLocationInitialState: OfficeLocationState = {
  data: null,
  loading: "idle",
  error: null,
  action: null,
};

export const getOfficeLocation: any = createAsyncThunk(
  "get/officeLocation",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.OFFICE_LOCATION.FIND_ALL,
        payload
      );
      return response.data.data;
    } catch (error: any) {
      console.log("Failed to fetch data.--", error);
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const addOfficeLocation: any = createAsyncThunk(
  "add/officeLocation",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        payload.id
          ? API_URLS.OFFICE_LOCATION.UPDATE
          : API_URLS.OFFICE_LOCATION.ADD,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const findOfficeLocation: any = createAsyncThunk(
  "find/officeLocation",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.OFFICE_LOCATION.FIND_BY_ID,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteOfficeLocation: any = createAsyncThunk(
  "delete/officeLocation",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.OFFICE_LOCATION.DELETE, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const statusOfficeLocation: any = createAsyncThunk(
  "status/officeLocation",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.OFFICE_LOCATION.CHANGE_STATUS,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const authSlice = createSlice({
  name: "reset",
  initialState: officeLocationInitialState,
  reducers: {
    resetAll: (state: any) => {
      console.log("Resetting office location state");
      state.loading = "idle";
      state.error = null;
      state.data = null;
      state.action = null;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getOfficeLocation.pending, (state) => {
      state.loading = "pending";
      state.data = null;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getOfficeLocation.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.data = action.payload;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getOfficeLocation.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.data = null;
      state.action = "list";
    });

    builder.addCase(addOfficeLocation.pending, (state) => {
      state.data = null;
      state.error = null;
      state.loading = "pending";
      state.action = "add";
    });
    builder.addCase(addOfficeLocation.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = "fulfilled";
      state.action = "add";
      state.error = null;
    });
    builder.addCase(addOfficeLocation.rejected, (state, action) => {
      state.error = action.payload as string;
      state.data = null;
      state.loading = "rejected";
      state.action = "add";
    });

    builder.addCase(findOfficeLocation.pending, (state) => {
      state.data = null;
      state.error = null;
      state.loading = "pending";
      state.action = "find";
    });
    builder.addCase(findOfficeLocation.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = "fulfilled";
      state.action = "find";
      state.error = null;
    });
    builder.addCase(findOfficeLocation.rejected, (state, action) => {
      state.error = action.payload as string;
      state.data = null;
      state.loading = "rejected";
      state.action = "find";
    });

    builder.addCase(deleteOfficeLocation.pending, (state) => {
      state.data = null;
      state.error = null;
      state.loading = "pending";
      state.action = "delete";
    });
    builder.addCase(deleteOfficeLocation.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = "fulfilled";
      state.action = "delete";
      state.error = null;
    });
    builder.addCase(deleteOfficeLocation.rejected, (state, action) => {
      state.error = action.payload as string;
      state.data = null;
      state.loading = "rejected";
      state.action = "delete";
    });

    builder.addCase(statusOfficeLocation.pending, (state) => {
      state.data = null;
      state.error = null;
      state.loading = "pending";
      state.action = "status";
    });
    builder.addCase(statusOfficeLocation.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = "fulfilled";
      state.action = "status";
      state.error = null;
    });
    builder.addCase(statusOfficeLocation.rejected, (state, action) => {
      state.error = action.payload as string;
      state.data = null;
      state.loading = "rejected";
      state.action = "status";
    });
  },
});

export const { resetAll } = authSlice.actions;

export default authSlice.reducer;
